/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Marketing Pages Utility
 *
 * Defines which routes are marketing/public-facing pages that should
 * always use light mode with brand theming, regardless of user preferences.
 */

/**
 * List of route patterns that are marketing/public-facing pages
 * These pages should always use light mode with Contractor Co-pilot brand theming
 */
const MARKETING_PAGE_PATTERNS = [
  "/", // Home page
  "/auth", // Auth pages (sign-in, sign-up, etc.)
  "/pricing",
  "/features",
  "/about",
  "/contact",
  "/blog",
  "/resources",
  "/integrations",
  "/solutions", // All solution pages
  "/roadmap",
  "/tools",
  "/templates",
  "/terms",
  "/privacy",
  "/cookies",
  "/legal",
  "/security",
  "/share",
  "/onboarding",
  "/post-auth",
] as const;

/**
 * Check if a given pathname is a marketing/public-facing page
 * @param pathname - The pathname to check (e.g., "/pricing" or "/auth/sign-in")
 * @returns true if the pathname matches a marketing page pattern
 */
// Routes under a marketing prefix that require the full authenticated
// provider stack (e.g. OrganizationProvider). These are checked first
// and excluded from the marketing shortcut.
const AUTHENTICATED_SUB_ROUTES = [
  "/onboarding/setup",
  "/onboarding/success",
  "/post-auth",
] as const;

export function isMarketingPage(pathname: string): boolean {
  const querySplit = pathname.split("?")[0];
  const hashSplit = querySplit?.split("#")[0] ?? "";
  const normalized = hashSplit.replace(/\/$/, "") || "/";

  if (normalized.startsWith("/app")) {
    return false;
  }

  if (
    AUTHENTICATED_SUB_ROUTES.some(
      (r) => normalized === r || normalized.startsWith(r + "/"),
    )
  ) {
    return false;
  }

  return MARKETING_PAGE_PATTERNS.some((pattern) => {
    if (pattern === "/" && normalized === "/") {
      return true;
    }

    if (pattern !== "/" && normalized.startsWith(pattern)) {
      return true;
    }

    return false;
  });
}

/**
 * Get the list of marketing page patterns (for reference/debugging)
 */
export function getMarketingPagePatterns(): readonly string[] {
  return MARKETING_PAGE_PATTERNS;
}
